import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, ShieldX, Search } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { api } from "../lib/api";

/**
 * Public certificate authenticity check — /verifica-adeverinta?nr=&cod=.
 * Pre-fills from the QR/link on the certificate; also supports manual entry.
 * Requires BOTH number + code (anti-enumeration).
 */
export default function VerificaAdeverinta() {
  const [params, setParams] = useSearchParams();
  const [nr, setNr] = useState(params.get("nr") || "");
  const [cod, setCod] = useState(params.get("cod") || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {valid, ...}

  const verify = useCallback(async (number, code) => {
    if (!number || !code) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await api.get("/certificates/verify", { params: { nr: number, cod: code } });
      setResult(r.data);
    } catch {
      setResult({ valid: false });
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-verify when the page is opened with both query params (QR scan / link).
  useEffect(() => {
    const qn = params.get("nr"), qc = params.get("cod");
    if (qn && qc) verify(qn, qc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setParams({ nr, cod });
    verify(nr.trim(), cod.trim());
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F6F6]">
      <SEO title="Verificare adeverință | FPED" noindex />
      <Header />
      <main className="flex-1 w-full max-w-xl mx-auto px-4 py-12">
        <div className="inline-flex items-center gap-2 text-[#965EA7] font-semibold mb-2">
          <ShieldCheck size={20} /> Verificare autenticitate adeverință
        </div>
        <p className="text-sm text-[#6B7280] mb-6">
          Introdu numărul adeverinței și codul de verificare (le găsești pe document).
        </p>

        <form onSubmit={submit} className="bg-white border border-[#E5E7EB] rounded-2xl p-5 md:p-6 space-y-4" data-testid="verify-form">
          <div>
            <label className="block text-sm font-medium text-[#373A45] mb-1">Număr adeverință</label>
            <input value={nr} onChange={(e) => setNr(e.target.value)} required
              className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm font-mono" placeholder="ADV-2026-0042" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#373A45] mb-1">Cod de verificare</label>
            <input value={cod} onChange={(e) => setCod(e.target.value)} required
              className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm font-mono uppercase" placeholder="A1B2C3D4" />
          </div>
          <button type="submit" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold px-5 py-2.5 text-sm disabled:opacity-60"
            data-testid="verify-submit">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />} Verifică
          </button>
        </form>

        {result && (
          result.valid ? (
            <div className="mt-6 bg-[#519872]/10 border border-[#519872]/30 rounded-2xl p-6" data-testid="verify-valid">
              <div className="inline-flex items-center gap-2 text-[#2F6B4E] font-bold text-lg">
                <ShieldCheck size={22} /> Adeverință validă
              </div>
              <dl className="mt-4 space-y-2 text-sm text-[#373A45]">
                <Row label="Titular" value={result.full_name} />
                <Row label="Webinar" value={result.webinar_title} />
                <Row label="Data participării" value={result.data_webinar} />
                <Row label="Număr" value={result.number} mono />
                {result.issued_at && <Row label="Emisă la" value={result.issued_at} />}
              </dl>
            </div>
          ) : (
            <div className="mt-6 bg-[#E63946]/10 border border-[#E63946]/30 rounded-2xl p-6 text-center" data-testid="verify-invalid">
              <ShieldX size={28} className="text-[#E63946] mx-auto mb-2" />
              <div className="font-bold text-[#7A1F26]">Adeverință negăsită</div>
              <p className="text-sm text-[#6B7280] mt-1">
                Numărul sau codul nu corespund unei adeverințe emise. Verifică datele introduse.
              </p>
            </div>
          )
        )}
      </main>
      <Footer />
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[#519872]/15 pb-2">
      <dt className="text-[#6B7280]">{label}</dt>
      <dd className={`text-right font-semibold ${mono ? "font-mono" : ""}`}>{value || "—"}</dd>
    </div>
  );
}
